import { useMemo, useState } from "react";
import UiIcon from "../UiIcon";
import { AdminEmpty, AdminTabs } from "../admin/AdminUi";
import { evaluationCopy } from "./evaluationCopy";
import "../../styles/cvi-evaluation.css";

const SECTIONS = ["continuous", "goals"];

// Manager's evaluation workspace: pick an employee, then evaluate them section by section under one
// compact context header (who, role, period, whether the period can still be edited). The sections
// themselves are rendered by the caller (`renderSection(section, member)`), unchanged.
export default function EvaluationWorkspace({
    members = [], activePeriod = null, language = "en", renderSection, renderAvatar = null,
    positionOf = () => null, periodLabel = label => label,
}) {
    const c = evaluationCopy(language);
    const [selectedId, setSelectedId] = useState(null);
    const [section, setSection] = useState(SECTIONS[0]);
    const [query, setQuery] = useState("");
    const selectedIndex = members.findIndex(m => m.id === selectedId);
    const selected = selectedIndex >= 0 ? members[selectedIndex] : null;
    const roleOf = member => c.roles[String(member?.role || "").toLowerCase()] || c.roles.specialist;
    const subtitleOf = member => [positionOf(member), roleOf(member)].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).join(" · ");
    const periodState = !activePeriod ? "none" : String(activePeriod.status || "active") === "active" ? "open" : "closed";
    const avatar = (member, size) => renderAvatar ? renderAvatar(member, size)
        : <span className="cvi-eval-avatar" style={{ width: size, height: size }} aria-hidden="true">{String(member?.name || "?").charAt(0)}</span>;

    const filtered = useMemo(() => {
        const q = query.trim().toLocaleLowerCase(language === "fr" ? "fr-CA" : "en-CA");
        if (!q) return members;
        return members.filter(m => [m.name, positionOf(m)].filter(Boolean).some(v => String(v).toLocaleLowerCase().includes(q)));
    }, [members, query, language, positionOf]);

    if (!selected) return <div className="cvi-eval" data-cvi-i18n="managed" lang={language}>
        <div className="cvi-eval-scroll">
            <header className="cvi-eval-intro">
                <div>
                    <h1>{c.title}</h1>
                    <p>{c.subtitle}</p>
                </div>
                <PeriodBadge c={c} state={periodState} label={activePeriod ? periodLabel(activePeriod.label) : null} />
            </header>
            {members.length === 0 ? <div className="adm-panel"><AdminEmpty icon="members" title={c.emptyTitle} description={c.emptyDescription} /></div> : <>
                {members.length > 6 && <label className="cvi-eval-search">
                    <span className="cvi-routine-sr">{c.search}</span>
                    <input type="search" value={query} placeholder={c.searchPlaceholder} onChange={e => setQuery(e.target.value)} aria-label={c.search} />
                </label>}
                <section className="cvi-eval-list" aria-label={c.count(members.length)}>
                    <div className="cvi-eval-list-head"><span>{c.count(filtered.length)}</span></div>
                    {filtered.length === 0 ? <p className="cvi-eval-nomatch" role="status">{c.noMatch(query.trim())}</p> : <ul>
                        {filtered.map(member => <li key={member.id}>
                            <button type="button" onClick={() => setSelectedId(member.id)} aria-label={c.open(member.name)}>
                                {avatar(member, 38)}
                                <span className="cvi-eval-person"><strong>{member.name}</strong><small>{subtitleOf(member)}</small></span>
                                <UiIcon name="chevronRight" size={16} />
                            </button>
                        </li>)}
                    </ul>}
                </section>
            </>}
        </div>
    </div>;

    const go = step => {
        const next = members[(selectedIndex + step + members.length) % members.length];
        if (next) setSelectedId(next.id);
    };

    return <div className="cvi-eval is-selected" data-cvi-i18n="managed" lang={language}>
        <header className="cvi-eval-context">
            <div className="cvi-eval-context-nav">
                <button type="button" className="adm-btn adm-btn--sm adm-btn--ghost" onClick={() => setSelectedId(null)}>
                    <UiIcon name="chevronLeft" size={14} />{c.back}</button>
                {members.length > 1 && <div className="cvi-eval-stepper">
                    <button type="button" className="adm-icon-btn" onClick={() => go(-1)} aria-label={c.previous}><UiIcon name="chevronLeft" size={15} /></button>
                    <span>{c.position(selectedIndex + 1, members.length)}</span>
                    <button type="button" className="adm-icon-btn" onClick={() => go(1)} aria-label={c.next}><UiIcon name="chevronRight" size={15} /></button>
                </div>}
            </div>
            <div className="cvi-eval-subject">
                {avatar(selected, 44)}
                <div className="cvi-eval-subject-copy">
                    <h1>{selected.name}</h1>
                    <p>{subtitleOf(selected)}</p>
                </div>
                <PeriodBadge c={c} state={periodState} label={activePeriod ? periodLabel(activePeriod.label) : null} withHelp />
            </div>
            <AdminTabs ariaLabel={c.sections} idPrefix="cvi-eval" value={section} onChange={setSection}
                items={[{ id: "continuous", label: c.responsibilities, icon: "routine" }, { id: "goals", label: c.goals, icon: "target" }]} />
        </header>
        <div className="cvi-eval-section" role="tabpanel" aria-labelledby={`cvi-eval-tab-${section}`}>
            {renderSection(section, selected)}
        </div>
    </div>;
}

function PeriodBadge({ c, state, label, withHelp = false }) {
    return <div className={`cvi-eval-period is-${state}`}>
        <span className="cvi-eval-period-label">{c.period}</span>
        <strong>{label || c.noPeriod}</strong>
        <span className="cvi-eval-period-state"><UiIcon name={state === "open" ? "edit" : state === "closed" ? "shield" : "schedule"} size={12} />{c.state[state]}</span>
        {withHelp && <span className="cvi-routine-sr">{c.stateHelp[state]}</span>}
    </div>;
}
